// Shared helpers for rendering CMS-defined workflow inputs in UI (VybyChat, PropsPanel, etc.)
// Goal: preserve CMS order, keep slot->input binding stable, and enforce list-slot UX rules.
//
// This file is intentionally UI-framework-agnostic: it only transforms (flow.inputs, inputsModel)
// into an ordered view-model that UI components can render.

export const DEFAULT_MEDIA_LIST_MAX = 20

export function isProbablyUrl(value) {
  if (typeof value !== 'string') return false
  const s = value.trim()
  if (!s) return false
  // Treat as URL only when it is unambiguously a URL-like scheme.
  return /^(https?:|data:|blob:)/i.test(s)
}

export function getInputLabel(inp) {
  if (!inp || typeof inp !== 'object') return ''
  if (inp.name) return inp.name
  const links = inp.links && typeof inp.links === 'object' ? inp.links : null
  if (links) {
    const firstKey = Object.keys(links)[0]
    const link = firstKey ? links[firstKey] : null
    if (link && link.input) return link.input
  }
  return inp.code || ''
}

export function getOrderedFlowInputs(flow, { display = null, includeHidden = false } = {}) {
  const inputs =
    flow && flow.inputs && typeof flow.inputs === 'object' ? flow.inputs : null
  if (!inputs) return []

  return Object.keys(inputs)
    .map((code) => ({ code, ...(inputs[code] || {}) }))
    .filter((inp) => {
      if (includeHidden) return true
      if (inp.display === 'hidden') return false
      if (!display) return true
      // Note: CMS sometimes leaves display empty; treat empty as eligible for all panels.
      if (!inp.display) return true
      return inp.display === display
    })
}

function clampList(list, max) {
  if (!Array.isArray(list)) return []
  if (!Number.isFinite(max) || max <= 0) return [...list]
  return list.slice(0, max)
}

function ensureTrailingEmptyListSlot(list, max) {
  const clamped = clampList(list, max)
  if (!Number.isFinite(max) || max <= 0) return clamped

  // Always show at least 1 slot. Append a trailing empty slot if:
  // - list is empty, OR
  // - last slot has a uri (meaning it is filled)
  //
  // But never exceed max slots.
  if (clamped.length >= max) return clamped
  if (!clamped.length) return [{ uri: '' }]

  const last = clamped[clamped.length - 1]
  const lastUri = typeof last?.uri === 'string' ? last.uri.trim() : ''
  if (lastUri) return [...clamped, { uri: '' }]
  return clamped
}

export function buildWorkflowInputLayout(
  flow,
  inputsModel,
  {
    display = null,
    includeHidden = false,
    mediaListMax = DEFAULT_MEDIA_LIST_MAX,
  } = {}
) {
  const ordered = getOrderedFlowInputs(flow, { display, includeHidden })

  const model = inputsModel && typeof inputsModel === 'object' ? inputsModel : {}

  // FieldVM (ordered list, preserves CMS order):
  // - kind: 'singleMedia' | 'listMedia' | 'control'
  // - slots: SlotVM[] (only for media kinds)
  const fields = ordered.map((inp) => {
    const type = inp.type
    const label = getInputLabel(inp)
    const value = model[inp.code]

    if (type === 'image' || type === 'video' || type === 'audio') {
      const v = typeof value === 'string' ? value : value == null ? '' : ''
      return {
        kind: 'singleMedia',
        code: inp.code,
        type,
        display: inp.display || null,
        label,
        slots: [
          {
            binding: { mode: 'single', code: inp.code },
            value: v,
            filled: !!(typeof v === 'string' && v.trim()),
          },
        ],
      }
    }

    if (type === 'images' || type === 'videos' || type === 'audios') {
      const list = ensureTrailingEmptyListSlot(value, mediaListMax)
      return {
        kind: 'listMedia',
        code: inp.code,
        type,
        display: inp.display || null,
        label,
        mediaListMax,
        slots: list.map((item, index) => {
          const uri = typeof item?.uri === 'string' ? item.uri : ''
          const filled = !!uri.trim()
          const isPlaceholder = !filled && index === list.length - 1
          return {
            binding: { mode: 'list', code: inp.code, index },
            value: uri,
            filled,
            placeholder: isPlaceholder,
            meta: {
              tag: typeof item?.tag === 'string' ? item.tag : null,
              position:
                typeof item?.position === 'string' ? item.position : null,
            },
          }
        }),
      }
    }

    // Multi-prompt: array of scene prompts (e.g. Kling 3.0 multi-shot).
    if (type === 'multi_prompt') {
      return {
        kind: 'multiPrompt',
        code: inp.code,
        type,
        display: inp.display || null,
        label,
        value: Array.isArray(value) ? value : [],
        maxSections: inp.specification?.constraints?.maxSections || 6,
        maxTotalDuration: inp.specification?.constraints?.maxTotalDuration || 15,
      }
    }

    // Non-media control (text/number/enum/slider/etc). Slots are not represented here.
    return {
      kind: 'control',
      code: inp.code,
      type,
      display: inp.display || null,
      label,
      value,
    }
  })

  return {
    orderedInputs: ordered,
    fields,
    meta: {
      display,
      includeHidden,
      mediaListMax,
    },
  }
}

// Dev helper: inspect layout builder from console without wiring UI yet.
// Usage (devtools): window.__vybyWorkflowInputLayout.buildWorkflowInputLayout(flow, inputs)
const __vybyIsDev =
  typeof process !== 'undefined' &&
  process.env &&
  process.env.NODE_ENV !== 'production'

if (__vybyIsDev && typeof window !== 'undefined') {
  window.__vybyWorkflowInputLayout = {
    DEFAULT_MEDIA_LIST_MAX,
    isProbablyUrl,
    getOrderedFlowInputs,
    buildWorkflowInputLayout,
  }
}

